/**
 * SearchFeaturePageShell.jsx — Epic 14 runtime adapter for search pages.
 *
 * Thin adapter that resolves page-surface metadata from searchPageMap by
 * `pageId` and passes the localized title and width contract to FeaturePageShell.
 *
 * The page map entry determines:
 *   - `titleKey`     — i18n key used to localize the page heading
 *   - `headerOwner`  — 'shell' renders the title; 'page' leaves heading to the page
 *   - `width`        — layout width contract passed to FeaturePageShell
 *
 * Props:
 *   pageId     {string}       — Stable id matching a SEARCH_PAGE_MAP entry (kind !== 'redirect')
 *   loading    {boolean}      — Forwarded to FeaturePageShell
 *   error      {string|null}  — Forwarded to FeaturePageShell
 *   actions    {ReactNode}    — Optional action bar alongside the heading
 *   description{string}       — Optional subtitle text
 *   children   {ReactNode}
 *   ...rest    — Any additional props forwarded to FeaturePageShell
 */

import React from 'react';
import { getSearchPageSurface } from '../../../app/routes/searchPageMap';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import FeaturePageShell from './FeaturePageShell';

const SearchFeaturePageShell = ({ pageId, loading, error, actions, description, children, ...rest }) => {
  const t = useLocalizedMessage();
  const surface = getSearchPageSurface(pageId);

  const title = surface.headerOwner === 'shell' && surface.titleKey
    ? t(surface.titleKey)
    : null;

  return (
    <FeaturePageShell
      title={title}
      width={surface.width || 'default'}
      loading={loading}
      error={error}
      actions={actions}
      description={description}
      {...rest}
    >
      {children}
    </FeaturePageShell>
  );
};

export default SearchFeaturePageShell;
